<script setup lang="ts">
import { equipCollection } from '@/core/equip'
import { useCommonStore } from '@/stores/modules/common'
import type { EquipHandbookType, EquipItemType, EquipQuality, EquipType } from '@/types/equip'
import { ref, watch, computed } from 'vue'
import { equipIcon, equipQualityList, equipQualityText, equipTypeList } from '../item/data'
import EquipInfo from '../item/components/EquipInfo.vue'
import { socketEmitAsync } from '@/utils/web-socket'

const commonStore = useCommonStore()

const userEquipCollection = ref<number[]>([])
const equipInfo = ref<EquipItemType>()
const initItemList = ref<EquipHandbookType[]>()
const itemList = computed<EquipHandbookType[]>(() => {
  return (
    initItemList.value?.filter((item) => {
      const specialNameList = item.attrs.special?.map((item) => item.name)
      const suitAffixNameList = item.attrs.suit?.attrs.affix?.map((item) => item.name)
      return (
        (filterType.value.length ? filterType.value.includes(item.type) : true) &&
        (filterQuality.value.length ? filterQuality.value.includes(item.quality) : true) &&
        item.name.includes(filterName.value) &&
        (filterAffix.value ? suitAffixNameList?.includes(filterAffix.value) || specialNameList?.includes(filterAffix.value) : true)
      )
    }) || []
  )
})
function showDetail(item: EquipHandbookType) {
  equipInfo.value = {
    id: String(item.equipId),
    equipId: item.equipId,
    name: item.name,
    quality: item.quality,
    type: item.type,
    attrs: item.attrs,
    reinforcedLevel: 0,
    isLock: false,
  }
}

const filterType = ref<EquipType[]>([])
const filterQuality = ref<EquipQuality[]>([])
const filterName = ref('')
const filterAffix = ref('')
function handleAddFilterType(type: EquipType) {
  const index = filterType.value.findIndex((item) => item === type)
  if (index !== -1) return filterType.value.splice(index, 1)
  filterType.value.push(type)
}
function handleAddFilterQuality(quality: EquipQuality) {
  const index = filterQuality.value.findIndex((item) => item === quality)
  if (index !== -1) return filterQuality.value.splice(index, 1)
  filterQuality.value.push(quality)
}
function handleResetFilter() {
  filterType.value = []
  filterQuality.value = []
  filterName.value = ''
  filterAffix.value = ''
}

watch(
  () => commonStore.path,
  async (val) => {
    if (val === 'EquipHandbook' && !initItemList.value) {
      initItemList.value = Object.entries(equipCollection).map((item) => {
        const [equipId, equipInfo] = item
        return {
          equipId: Number(equipId),
          ...equipInfo,
          quality: equipInfo.quality[equipInfo.quality.length - 1]!,
          qualityList: equipInfo.quality,
        }
      })
      const res = await socketEmitAsync<number[]>({ type: 'getUserEquipCollection' })
      userEquipCollection.value = res
    }
  },
)
</script>

<template>
  <div class="equip-handbook-page page">
    <h2>图鉴</h2>

    <div class="main">
      <div class="list border-wrap">
        <h4>装备列表</h4>

        <div class="item-btn-wrap btn-wrap">
          <CommonBtn
            v-for="item of itemList"
            :key="item.equipId"
            size="small"
            :type="userEquipCollection.includes(item.equipId) ? 'primary' : 'info'"
            @done="showDetail(item)"
            :style="{ position: 'relative', borderColor: equipInfo?.equipId === item.equipId ? 'red' : '' }"
          >
            {{ equipIcon[item.type] }} <label :class="item.quality"> {{ item.name }}</label>
          </CommonBtn>
        </div>
      </div>
      <div class="info">
        <div class="action border-wrap">
          <h4>操作</h4>
          <el-popover class="affix" popper-class="item-filter-wrap" placement="top" width="100%" trigger="click" :teleported="false">
            <template #reference>
              <el-button size="small" type="primary" plain>筛选</el-button>
            </template>
            <p>装备类型</p>
            <div style="padding-left: 6px">
              <el-button
                size="small"
                type="primary"
                plain
                class="item"
                v-for="item of (['weapon', 'helmet', 'armor', 'shoes', 'jewelry'] as EquipType[])"
                :key="item"
                @click="handleAddFilterType(item)"
                :style="{ width: '40px', borderColor: filterType.includes(item) ? 'red' : '' }"
              >
                {{ equipIcon[item] }}
              </el-button>
            </div>
            <div style="padding-left: 6px; margin-top: 8px">
              <el-button
                size="small"
                type="primary"
                plain
                class="item"
                v-for="item of (['belt', 'glove', 'pants',  'necklace', 'medal'] as EquipType[])"
                :key="item"
                @click="handleAddFilterType(item)"
                :style="{ width: '40px', borderColor: filterType.includes(item) ? 'red' : '' }"
              >
                {{ equipIcon[item] }}
              </el-button>
            </div>
            <p>装备品质</p>
            <div>
              <el-button
                size="small"
                type="primary"
                plain
                class="item"
                v-for="item of equipQualityList.slice(0, 5)"
                :key="item"
                @click="handleAddFilterQuality(item)"
                :style="{ borderColor: filterQuality.includes(item) ? 'red' : '' }"
              >
                <span :class="item">{{ equipQualityText[item] }}</span>
              </el-button>
            </div>
            <div>
              <el-button
                size="small"
                type="primary"
                plain
                class="item"
                v-for="item of equipQualityList.slice(5)"
                :key="item"
                @click="handleAddFilterQuality(item)"
                :style="{ borderColor: filterQuality.includes(item) ? 'red' : '' }"
                style="margin-top: 10px"
              >
                <span :class="item">{{ equipQualityText[item] }}</span>
              </el-button>
            </div>
            <p>搜索名称</p>
            <el-input size="small" v-model="filterName"></el-input>
            <p>搜索词条</p>
            <el-input size="small" v-model="filterAffix"></el-input>
            <br />
            <br />
            <el-button size="small" type="success" plain @click="handleResetFilter">重置</el-button>
          </el-popover>
        </div>
        <div class="detail border-wrap">
          <h4>装备信息</h4>
          <template v-if="equipInfo">
            <EquipInfo :equipInfo="equipInfo" :showAction="false" />
          </template>
        </div>
      </div>
    </div>

    <FooterBar />
  </div>
</template>

<style lang="scss" scoped>
.equip-handbook-page {
  padding-top: 0;
  height: 100%;
  display: flex;
  flex-flow: column nowrap;
  overflow: hidden;
  .border-wrap {
    padding: 0 10px 10px 10px;
  }
  .main {
    flex: 1;
    display: flex;
    justify-content: space-between;
    overflow: hidden;
  }

  .list {
    width: calc(50% - 5px);
    padding: 0 4px;
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    .item-btn-wrap {
      height: 100%;
      overflow: auto;
      :deep() {
        .el-button:focus,
        .el-button:hover {
          background-color: unset;
        }
      }
    }
    .common-btn-wrap {
      margin-bottom: 10px;
    }
  }
  .quality-list {
    button {
      &:nth-child(7) {
        margin-top: 8px;
        margin-left: 0;
      }
    }
  }
  .info {
    height: 100%;
    width: calc(50% - 5px);
    display: flex;
    flex-flow: column;
    margin-bottom: 10px;
    flex-shrink: 0;
    .action {
      margin-bottom: 10px;
      & > button {
        width: 100%;
        margin-left: 0;
        margin-bottom: 10px;
      }
    }
    .detail {
      flex: 1;
      overflow: hidden;
      display: flex;
      flex-flow: column;
    }
  }

  :deep() {
    .item-btn-wrap,
    .item-filter-wrap {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
  }
}
</style>
